import { useState, type FormEvent } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { KNOWN_PLANS } from '@wf/shared'
import { useAuth } from '../app/auth'
import { AdminStatsPanel } from './AdminStatsPanel'
import { AdminWorkshopRow } from './AdminWorkshopRow'
import { ChefActivationReveal } from './ChefActivationReveal'
import { useAdminWorkshops, useCreateWorkshop } from './useAdmin'

const EMPTY = { workshop_name: '', chef_name: '', username: '', plan: 'test', email: '' }

/**
 * App-Verwaltung (Betreiber / Super-Admin) — Funktions-Parität zum V1-Admin: Werkstatt-Lizenzen anlegen und
 * verwalten (Tarif, aktiv, Chef-Passwort-Reset, löschen). Mandantenübergreifend; der Server erzwingt is_superadmin
 * auf jeder /admin/*-Route, dieser Client zeigt das Panel nur dem Super-Admin (Nav + Guard hier).
 */
export function AdminPage() {
  const { user } = useAuth()
  const { data, isLoading, error } = useAdminWorkshops()
  const create = useCreateWorkshop()
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState(EMPTY)
  // Aktivierungscode der zuletzt angelegten Werkstatt (Klartext nur EINMAL — zum Weitergeben an den neuen Chef).
  const [created, setCreated] = useState<{ code: string; chefUsername: string } | null>(null)

  // Defensiv: nur der Betreiber. Die Nav blendet den Reiter für alle anderen ohnehin aus.
  if (user && !user.isSuperadmin) return <Navigate to="/overview" replace />

  const set = (key: keyof typeof form, value: string) => setForm((f) => ({ ...f, [key]: value }))

  function submitCreate(event: FormEvent) {
    event.preventDefault()
    create.mutate(
      {
        workshop_name: form.workshop_name.trim(),
        chef_name: form.chef_name.trim(),
        username: form.username.trim().toLowerCase(),
        plan: form.plan,
        email: form.email.trim() || undefined,
      },
      {
        onSuccess: (res) => {
          setCreated({ code: res.activation.code, chefUsername: res.chefUsername })
          setForm(EMPTY)
          setOpen(false)
        },
      },
    )
  }

  const workshops = data?.workshops ?? []

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">App-Verwaltung</h2>
          <p className="sub">Werkstatt-Lizenzen anlegen und verwalten (Betreiber)</p>
        </div>
        <div className="wf-adminrow__actions">
          <Link className="wf-btn wf-btn--ghost" to="/admin/audit">
            Protokoll
          </Link>
          <button
            type="button"
            className="wf-btn wf-btn--primary"
            onClick={() => {
              setOpen((o) => !o)
              setCreated(null)
            }}
          >
            {open ? '✕ Abbrechen' : '＋ Neue Werkstatt'}
          </button>
        </div>
      </div>

      {created && (
        <div className="wf-card wf-adminform">
          <ChefActivationReveal code={created.code} chefUsername={created.chefUsername} heading="✅ Werkstatt angelegt" />
          <div className="wf-form__actions">
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => setCreated(null)}>
              Fertig
            </button>
          </div>
        </div>
      )}

      {open && (
        <form className="wf-card wf-adminform" onSubmit={submitCreate}>
          <div className="two">
            <label className="wf-field">
              <span className="wf-label">Werkstatt-Name</span>
              <input className="wf-input" value={form.workshop_name} onChange={(e) => set('workshop_name', e.target.value)} required />
            </label>
            <label className="wf-field">
              <span className="wf-label">Chef-Name</span>
              <input className="wf-input" value={form.chef_name} onChange={(e) => set('chef_name', e.target.value)} required />
            </label>
          </div>
          <div className="two">
            <label className="wf-field">
              <span className="wf-label">Chef-Login (Benutzername)</span>
              <input
                className="wf-input"
                value={form.username}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => set('username', e.target.value.toLowerCase())}
                required
              />
            </label>
            <label className="wf-field">
              <span className="wf-label">Tarif</span>
              <select className="wf-input" value={form.plan} onChange={(e) => set('plan', e.target.value)}>
                {KNOWN_PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="wf-field">
            <span className="wf-label">E-Mail (optional)</span>
            <input className="wf-input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </label>
          <p className="hint">
            Der Chef bekommt kein Passwort von dir — nach dem Anlegen erhältst du einen Aktivierungscode (+ Magic-Link)
            zum Weitergeben. Der Chef setzt sein Passwort selbst.
          </p>
          {create.isError && (
            <div className="error" role="alert">
              {(create.error as Error).message}
            </div>
          )}
          <div className="wf-form__actions">
            <button type="submit" className="wf-btn wf-btn--primary" disabled={create.isPending}>
              Werkstatt anlegen
            </button>
          </div>
        </form>
      )}

      <AdminStatsPanel />

      <div className="wf-sectionhead">
        <h3>Werkstätten</h3>
        <span className="wf-count">{workshops.length}</span>
      </div>

      {isLoading ? (
        <div className="muted">Werkstätten werden geladen…</div>
      ) : error ? (
        <div className="error" role="alert">
          Werkstätten konnten nicht geladen werden.
        </div>
      ) : workshops.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">Noch keine Werkstatt</div>
          <div className="wf-empty__text">Leg oben die erste Werkstatt-Lizenz an.</div>
        </div>
      ) : (
        <div className="wf-adminlist">
          {workshops.map((w) => (
            <AdminWorkshopRow key={w.id} workshop={w} />
          ))}
        </div>
      )}
    </section>
  )
}
